/**
 * Gift With Purchase Manager & Product Offered Manager
 * 
 * Cette classe gère l'ajout automatique de produits cadeaux (GWP) lorsque
 * certains produits sont ajoutés au panier, ainsi que les produits offerts
 * basés sur les metaobjets.
 */
class GiftWithPurchaseManager {
    constructor() {
        this.processedGWP = new Set(); // Pour éviter de traiter plusieurs fois le même produit cadeau
        this.storagePrefix = 'removed_product_'; // Préfixe pour les éléments localStorage
        this.blockDurationMinutes = 10; // Durée de blocage en minutes
        this.isProcessingCartChange = false; // Flag pour éviter les traitements simultanés
        this.debugMode = false; // Mode débogage désactivé en production

        // Récupération des données du metaobjet depuis l'objet theme
        this.thresholdAmount = window.theme && window.theme.productOffered ?
            parseFloat(window.theme.productOffered.thresholdAmount) : 69;
        this.productOfferedId = window.theme && window.theme.productOffered && window.theme.productOffered.id != null ?
            window.theme.productOffered.id.toString() : '';
        this.promoCode = window.theme && window.theme.productOffered ?
            window.theme.productOffered.promoCode : '';

        // Intercepter toutes les requêtes fetch pour bloquer les ajouts non désirés
        this.installFetchInterceptor();

        this.init();
    }

    /**
     * Installe un intercepteur global fetch pour bloquer les ajouts du produit pendant la période de blocage
     */
    installFetchInterceptor() {
        // Sauvegarder la référence à la fonction fetch originale
        const originalFetch = window.fetch;
        const self = this;

        // Remplacer fetch par notre version qui vérifie et bloque certaines requêtes
        window.fetch = function (input, init) {
            // Vérifier si c'est une requête d'ajout au panier
            if (input && input.toString().includes('/cart/add') && init && init.body) {
                try {
                    // Essayer de parser le corps de la requête
                    const bodyData = JSON.parse(init.body);

                    // Rechercher si la requête tente d'ajouter le produit offert
                    if (bodyData && bodyData.items) {
                        for (let item of bodyData.items) {
                            if (item.id && item.id.toString() === self.productOfferedId) {
                                // Vérifier si le produit est actuellement bloqué
                                if (self.isProductBlocked(self.productOfferedId)) {
                                    if (self.debugMode) console.error(`Interception: tentative d'ajout du produit bloqué empêchée`);

                                    // Créer une réponse simulée pour indiquer que l'ajout a été bloqué
                                    return Promise.resolve({
                                        ok: false,
                                        status: 200,
                                        json: () => Promise.resolve({
                                            status: "blocked",
                                            message: "Product is blocked for addition"
                                        })
                                    });
                                }
                            }
                        }
                    }
                } catch (e) {
                    // Silencieux en production
                }
            }

            // Appeler la fonction fetch originale pour toutes les autres requêtes
            return originalFetch.apply(window, arguments);
        };
    }

    /**
     * Nettoie les entrées localStorage pour les tests
     */
    cleanupStorage() {
        if (this.debugMode) console.log("Nettoyage du localStorage pour les tests");
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(this.storagePrefix)) {
                localStorage.removeItem(key);
            }
        }
    }

    /**
     * Initialise les écouteurs d'événements et vérifie le panier actuel
     */
    init() {
        // Intercepter les clics sur les boutons de suppression AVANT que la requête ne soit envoyée
        document.addEventListener('click', (event) => {
            const removeLink = event.target.closest('[data-remove-key]');
            if (!removeLink) return;

            const itemKey = removeLink.getAttribute('data-remove-key');

            // Vérifier si cet article correspond au produit offert
            fetch(`${window.theme.routes.cart}.js`)
                .then(response => response.json())
                .then(cart => {
                    cart.items.forEach(item => {
                        if (item.key === itemKey && item.variant_id.toString() === this.productOfferedId) {
                            // Bloquer immédiatement
                            this.blockProduct(this.productOfferedId);

                            // Double vérification du blocage
                            if (this.isProductBlocked(this.productOfferedId)) {
                                // Ajouter une classe au <body> pour désactiver temporairement l'ajout automatique
                                document.body.classList.add('product-offered-blocked');

                                // Planifier la suppression de la classe après la fin du blocage
                                setTimeout(() => {
                                    document.body.classList.remove('product-offered-blocked');
                                }, this.blockDurationMinutes * 60 * 1000);
                            }
                        }
                    });
                })
                .catch(error => {
                    if (this.debugMode) console.error("Erreur lors de la vérification du produit supprimé:", error);
                });
        }, true); // <- Utiliser la phase de capture

        // Écouter les événements d'ajout/modification du panier avec un délai plus important
        document.addEventListener('theme:cart:change', (event) => {
            // Vérifier s'il y a un blocage actif pour décider comment traiter l'événement
            if (this.isProductBlocked(this.productOfferedId)) {
                return;
            }

            if (event.detail && event.detail.cart) {
                if (this.isProcessingCartChange) {
                    return;
                }

                this.isProcessingCartChange = true;

                // Délai pour laisser le temps au blocage de se mettre en place
                setTimeout(() => {
                    if (!this.isProductBlocked(this.productOfferedId)) {
                        this.handleCartChange(event.detail.cart);
                    }
                    this.isProcessingCartChange = false;
                }, 1000);
            }
        });

        // Vérifier si un produit offert doit être automatiquement supprimé
        document.addEventListener('theme:cart:updated', (event) => {
            // Si le produit est bloqué, vérifier s'il a été ajouté malgré le blocage et le supprimer
            if (this.isProductBlocked(this.productOfferedId) && event.detail && event.detail.cart) {
                const cart = event.detail.cart;

                cart.items.forEach(item => {
                    if (item.variant_id.toString() === this.productOfferedId) {
                        // Supprimer immédiatement le produit du panier
                        fetch(`/cart/change.js`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                id: item.key,
                                quantity: 0
                            })
                        })
                            .then(response => response.json())
                            .then(() => {
                                // Rafraîchir le panier sans le produit
                                document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
                            })
                            .catch(error => {
                                if (this.debugMode) console.error("Erreur lors de la suppression du produit bloqué:", error);
                            });
                    }
                });
            }
        });

        // Vérifier le panier actuel au chargement de la page (avec délai pour laisser la page se charger)
        setTimeout(() => {
            this.checkCurrentCart();
        }, 500);
    }

    /**
     * Bloque un produit pour empêcher son ajout automatique
     * @param {string} productId - ID du produit à bloquer
     */
    blockProduct(productId) {
        if (!productId) return;

        const storageKey = this.storagePrefix + productId;
        const expiryTime = Date.now() + (this.blockDurationMinutes * 60 * 1000);

        try {
            // D'abord supprimer l'entrée si elle existe déjà
            localStorage.removeItem(storageKey);

            // Ensuite créer une nouvelle entrée
            localStorage.setItem(storageKey, expiryTime.toString());

            // Autre vérification avec la fonction isProductBlocked
            if (!this.isProductBlocked(productId) && this.debugMode) {
                console.error(`Échec du blocage pour le produit ${productId}`);
            }

            // Ajouter un attribut de comportement pour indiquer à tout le site que ce produit est bloqué
            this.updateGlobalBlockingIndicator(true);
        } catch (e) {
            if (this.debugMode) console.error(`Erreur lors du blocage du produit:`, e);

            // Essayer une alternative en cas d'erreur
            try {
                window.sessionStorage.setItem(storageKey, expiryTime.toString());
            } catch (e2) {
                if (this.debugMode) console.error(`Échec également avec sessionStorage:`, e2);
            }
        }
    }

    /**
     * Met à jour les indicateurs globaux de blocage
     * @param {boolean} isBlocked - Si le produit est bloqué ou non
     */
    updateGlobalBlockingIndicator(isBlocked) {
        try {
            // Créer un attribut global pour indiquer que le produit est bloqué
            window.productOfferedBlocked = isBlocked;

            // Mettre à jour l'attribut de données sur les éléments pertinents
            const productOfferedElements = document.querySelectorAll('[data-product-offered]');
            productOfferedElements.forEach(el => {
                el.setAttribute('data-product-offered-blocked', isBlocked.toString());
            });
        } catch (e) {
            if (this.debugMode) console.error(`Erreur lors de la mise à jour des indicateurs de blocage:`, e);
        }
    }

    /**
     * Vérifie si un produit est actuellement bloqué
     * @param {string} productId - ID du produit à vérifier
     * @returns {boolean} - true si le produit est bloqué
     */
    isProductBlocked(productId) {
        if (!productId) return false;

        const storageKey = this.storagePrefix + productId;
        let isBlocked = false;

        try {
            // Essayer d'abord localStorage
            const storedValue = localStorage.getItem(storageKey);

            if (storedValue) {
                const expiryTime = parseInt(storedValue, 10);
                isBlocked = expiryTime > Date.now();

                if (!isBlocked) {
                    localStorage.removeItem(storageKey); // Nettoyage
                    this.updateGlobalBlockingIndicator(false); // Mettre à jour les indicateurs
                }
            } else {
                // Essayer sessionStorage en fallback
                const sessionValue = window.sessionStorage.getItem(storageKey);
                if (sessionValue) {
                    const expiryTime = parseInt(sessionValue, 10);
                    isBlocked = expiryTime > Date.now();

                    if (!isBlocked) {
                        window.sessionStorage.removeItem(storageKey); // Nettoyage
                        this.updateGlobalBlockingIndicator(false); // Mettre à jour les indicateurs
                    }
                }
            }

            return isBlocked;
        } catch (e) {
            if (this.debugMode) console.error(`Erreur lors de la vérification du blocage:`, e);
            return false;
        }
    }

    /**
     * Obtient le temps restant avant la fin du blocage
     * @param {string} productId - ID du produit
     * @returns {number} - Temps restant en secondes
     */
    getBlockTimeLeft(productId) {
        if (!productId) return 0;

        const storageKey = this.storagePrefix + productId;
        try {
            // Vérifier localStorage
            const storedValue = localStorage.getItem(storageKey);
            if (storedValue) {
                const expiryTime = parseInt(storedValue, 10);
                return Math.max(0, Math.floor((expiryTime - Date.now()) / 1000));
            }

            // Fallback: vérifier sessionStorage
            const sessionValue = window.sessionStorage.getItem(storageKey);
            if (sessionValue) {
                const expiryTime = parseInt(sessionValue, 10);
                return Math.max(0, Math.floor((expiryTime - Date.now()) / 1000));
            }

            return 0;
        } catch (e) {
            if (this.debugMode) console.error(`Erreur lors du calcul du temps restant:`, e);
            return 0;
        }
    }

    /**
     * Gère les changements dans le panier
     * @param {Object} cart - L'objet panier de Shopify
     */
    handleCartChange(cart) {
        // Vérification critique du blocage avant de procéder
        if (this.isProductBlocked(this.productOfferedId)) {
            return;
        }

        // Traiter les produits offerts
        this.addProductOfferedIfNeeded(cart);
        this.applyAutomaticDiscount(cart);
    }

    /**
     * Vérifie le panier actuel et traite les produits cadeaux/offerts si nécessaire
     */
    checkCurrentCart() {
        // Vérification préalable du blocage
        if (this.isProductBlocked(this.productOfferedId)) {
            return;
        }

        fetch(`${window.theme.routes.cart}.js`)
            .then((response) => response.json())
            .then((cart) => {
                this.handleCartChange(cart);
            })
            .catch((error) => {
                if (this.debugMode) console.error(`Erreur lors de la récupération du panier:`, error);
            });
    }

    /**
     * Vérifie si le montant du panier atteint le seuil requis
     * @param {Object} cart - L'objet panier de Shopify
     * @returns {boolean} - True si le seuil est atteint, false sinon
     */
    cartMeetsThreshold(cart) {
        const cartTotalPrice = cart.total_price / 100; // Convertir en euros (retirer les centimes)
        return cartTotalPrice >= this.thresholdAmount;
    }

    /**
     * Ajoute le produit offert au panier si les conditions sont remplies
     * @param {Object} cart - L'objet panier de Shopify
     */
    addProductOfferedIfNeeded(cart) {
        // Vérifier si on a un ID de produit offert valide
        if (!this.productOfferedId) return;

        // Vérification cruciale du blocage
        if (this.isProductBlocked(this.productOfferedId)) return;

        // Si le corps a la classe de blocage, c'est un autre niveau de sécurité
        if (document.body.classList.contains('product-offered-blocked')) return;

        // Vérifier d'abord si le montant du panier atteint le seuil requis
        if (!this.cartMeetsThreshold(cart)) return;

        // Vérifier si le panier contient des produits éligibles (avec livraison, pas de SKU restreint)
        const hasShippableProduct = cart.items.some(item => item.requires_shipping);
        if (!hasShippableProduct) return;

        // Vérifier si le panier contient des SKUs restreints
        const hasRestrictedSku = cart.items.some(item =>
            item.sku === 'CCS69' || item.sku === 'CCS138'
        );
        if (hasRestrictedSku) return;

        // Vérifier si le produit est déjà dans le panier
        const productInCart = cart.items.some(item =>
            item.variant_id.toString() === this.productOfferedId
        );

        if (!productInCart) {
            // Vérification finale avant l'ajout
            if (!window.productOfferedBlocked) {
                // Ajouter le produit offert au panier
                this.addProductOfferedToCart(this.productOfferedId);
            }
        }
    }

    /**
     * Applique automatiquement le code de réduction si les conditions sont remplies
     * @param {Object} cart - L'objet panier de Shopify
     */
    applyAutomaticDiscount(cart) {
        // Vérifier si on a un code promo
        if (!this.promoCode) return;

        // Vérifier si le code n'est pas déjà appliqué
        if (!cart.attributes || !cart.attributes.applied_discount || cart.attributes.applied_discount !== this.promoCode) {
            // Appliquer le code de réduction
            this.applyDiscountCode(this.promoCode);
        }
    }

    /**
     * Applique un code de réduction au panier
     * @param {string} discountCode - Le code de réduction à appliquer
     */
    applyDiscountCode(discountCode) {
        fetch(`/discount/${discountCode}`)
            .then(response => {
                // Marquer le code comme appliqué
                this.setCartAttribute('applied_discount', discountCode);
            })
            .catch(error => {
                if (this.debugMode) console.error("Erreur lors de l'application du code de réduction:", error);
            });
    }

    /**
     * Définit un attribut de panier
     * @param {string} key - Clé de l'attribut
     * @param {string} value - Valeur de l'attribut
     */
    setCartAttribute(key, value) {
        fetch('/cart/update.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                attributes: {
                    [key]: value
                }
            })
        })
            .then(response => response.json())
            .catch(error => {
                if (this.debugMode) console.error("Erreur lors de la définition de l'attribut de panier:", error);
            });
    }

    /**
     * Ajoute le produit offert au panier
     * @param {string} productId - L'ID du produit offert
     */
    addProductOfferedToCart(productId) {
        // Dernière vérification du blocage avant d'ajouter
        if (this.isProductBlocked(productId)) return;

        fetch(`${window.theme.routes.root_url}cart/add.js`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                items: [
                    {
                        id: productId,
                        quantity: 1,
                        properties: {
                            _product_offered: 'true'
                        },
                    },
                ],
            }),
        })
            .then((response) => response.json())
            .then(() => {
                // Rafraîchir le panier pour afficher le produit offert
                document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
            })
            .catch((error) => {
                if (this.debugMode) console.error("Erreur lors de l'ajout du produit offert:", error);
            });
    }
}

// Initialiser le gestionnaire de produits cadeaux lorsque le DOM est chargé
document.addEventListener('DOMContentLoaded', () => {
    window.giftWithPurchaseManager = new GiftWithPurchaseManager();
}); 